/**
 * Hilfsfunktionen für die Verarbeitung von Zeitangaben
 */

/**
 * Konvertiert eine Dezimalzahl in Stunden und Minuten
 * Unterstützt sowohl Komma als auch Punkt als Dezimaltrennzeichen
 * Beispiele:
 * - "5,7" oder "5.7" wird zu 5 Stunden und 42 Minuten (5:42)
 * - "2,25" oder "2.25" wird zu 2 Stunden und 15 Minuten (2:15)
 * 
 * @param {string|number} value - Die zu konvertierende Zahl (als String oder Zahl)
 * @returns {object} - Ein Objekt mit Stunden, Minuten und der Gesamtminuten
 */
export function parseDecimalHours(value) {
  if (value === null || value === undefined || value === "") {
    return { hours: 0, minutes: 0, totalMinutes: 0 };
  }

  // Konvertiere zu String für die Verarbeitung
  let valueStr = value.toString();
  
  // Ersetze Komma durch Punkt für konsistente Verarbeitung
  valueStr = valueStr.replace(",", ".");
  
  // Konvertiere zu Zahl
  const decimalValue = parseFloat(valueStr);
  
  if (isNaN(decimalValue)) {
    console.warn(`Ungültiger Zeitwert: ${value}`);
    return { hours: 0, minutes: 0, totalMinutes: 0 };
  }
  
  // Berechne Stunden (ganzzahliger Teil)
  const hours = Math.floor(decimalValue);
  
  // Berechne Minuten (Dezimalteil * 60)
  const minutes = Math.round((decimalValue - hours) * 60);
  
  // Berechne Gesamtminuten für einfache Vergleiche
  const totalMinutes = hours * 60 + minutes;
  
  return { hours, minutes, totalMinutes };
}

/**
 * Formatiert Stunden und Minuten als String im Format "HH:MM"
 * 
 * @param {number} hours - Stunden
 * @param {number} minutes - Minuten
 * @returns {string} Formatierter String im Format "HH:MM"
 */
export const formatHoursAndMinutes = (hours, minutes) => {
  const formattedHours = String(Math.floor(hours)).padStart(2, '0');
  const formattedMinutes = String(Math.floor(minutes)).padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}`;
};

/**
 * Wandelt einen Dezimalwert (Stunden) in einen formatierten Zeit-String (HH:MM) um
 * 
 * @param {string|number} decimalHours - Dezimaler Stundenwert (z.B. "5,7", "5.7", 5.7)
 * @returns {string} Formatierter Zeit-String (z.B. "05:42")
 */
export const decimalHoursToTimeString = (decimalHours) => {
  const { hours, minutes } = parseDecimalHours(decimalHours);
  return formatHoursAndMinutes(hours, minutes);
};

/**
 * Konvertiert einen Dezimalstundenwert in ein formatiertes Stunden:Minuten-Format
 * Alias für decimalHoursToTimeString für Kompatibilität
 * 
 * @param {number} decimalHours - Dezimalstundenwert (z.B. 5.7)
 * @returns {string} - Formatierter String im Format "HH:MM"
 */
export const decimalHoursToTime = (decimalHours) => {
  return decimalHoursToTimeString(decimalHours);
};

/**
 * Konvertiert eine Dezimalzahl in einen formatierten Zeit-String (HH:MM)
 * Alias für decimalHoursToTimeString für Kompatibilität
 * 
 * @param {string|number} value - Die zu konvertierende Zahl (als String oder Zahl)
 * @returns {string} - Formatierter Zeit-String (HH:MM)
 */
export const formatDecimalHoursToTimeString = (value) => {
  return decimalHoursToTimeString(value);
};

/**
 * Wandelt einen Zeit-String (HH:MM) in Dezimalstunden um
 * 
 * @param {string} timeString - Zeit-String im Format "HH:MM"
 * @returns {number} Dezimalstunden
 */
export const timeStringToDecimalHours = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return 0;
  }
  
  const parts = timeString.split(':');
  if (parts.length < 2) {
    return 0;
  }
  
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  
  if (isNaN(hours) || isNaN(minutes)) {
    return 0;
  }
  
  return hours + (minutes / 60);
};

/**
 * Konvertiert Stunden:Minuten-Format zu Dezimalstunden
 * Alias für timeStringToDecimalHours für Kompatibilität
 * 
 * @param {string} timeString - Zeit im Format "HH:MM"
 * @returns {number} - Dezimalstundenwert
 */
export const timeToDecimalHours = (timeString) => {
  return timeStringToDecimalHours(timeString);
};

/**
 * Normalisiert und verarbeitet ungewöhnliche Zeitformate
 * Unterstützt auch Formate wie "YYYY-MM-DD HH.FRACTION:mm" (z.B. "2025-01-09 00.2972222222222222:00")
 * 
 * @param {string} timeString - Eine Zeitangabe in beliebigem Format
 * @returns {object} - Ein Objekt mit normalisierten Werten {date, time}
 */
export const normalizeTimeFormat = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return { date: '', time: '' };
  }

  // Für Format "YYYY-MM-DD HH.FRACTION:mm"
  const fracHourRegex = /^(\d{4}-\d{2}-\d{2})\s+(\d{1,2})\.(\d+):(\d{2})$/;
  const match = timeString.match(fracHourRegex);
  
  if (match) {
    const [_, dateStr, hourStr, fracStr, minStr] = match;
    
    // Wandle fraktionale Stunden in Minuten um
    const hour = parseInt(hourStr, 10);
    const fracMinutes = Math.round(parseFloat(`0.${fracStr}`) * 60);
    
    // Normalisiere die Zeit: Wenn fracMinutes >= 60, erhöhe die Stunde
    const totalMinutes = parseInt(minStr, 10) + fracMinutes;
    const newHour = hour + Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    
    // Formatiere die Zeit zurück im HH:MM-Format
    const timeStr = `${String(newHour).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    
    return {
      date: dateStr,
      time: timeStr
    };
  }
  
  // Für Standard-Format ISO "YYYY-MM-DDTHH:MM:SS" oder "YYYY-MM-DD HH:MM:SS"
  const standardRegex = /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/;
  const stdMatch = timeString.match(standardRegex);
  
  if (stdMatch) {
    return {
      date: stdMatch[1],
      time: stdMatch[2]
    };
  }
  
  // Fallback für unbekannte Formate - versuche bestmöglich zu parsen
  try {
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      return {
        date: date.toISOString().split('T')[0],
        time: date.toTimeString().substring(0, 5)
      };
    }
  } catch (e) {
    console.warn(`Konnte Zeitformat nicht parsen: ${timeString}`, e);
  }
  
  return { date: '', time: '' };
};

/**
 * Extrahiert und normalisiert die Zeitkomponente aus einem Zeitstring in beliebigem Format
 * 
 * @param {string} timeString - Zeitstring in beliebigem Format
 * @returns {string} - Normalisierte Zeitkomponente im Format "HH:MM"
 */
export const extractNormalizedTime = (timeString) => {
  return normalizeTimeFormat(timeString).time;
};

/**
 * Überprüft, ob eine Zeiterfassung als auffällig oder unauffällig eingestuft werden soll
 * 
 * @param {object} timeEntry - Der zu prüfende Zeiteintrag
 * @param {array} rosterData - Array mit Dienstplan-Einträgen für den entsprechenden Tag
 * @param {array} annualPlanData - Array mit Jahresplan-Einträgen
 * @returns {object} - Ein Objekt mit { isConspicuous: boolean, reason: string }
 */
export const checkTimeConspicuity = (timeEntry, rosterData, annualPlanData) => {
  // Wenn kein Zeiteintrag vorliegt, ist es immer auffällig
  if (!timeEntry || !timeEntry.date) {
    return { isConspicuous: true, reason: 'Kein gültiger Zeiteintrag vorhanden' };
  }

  const date = timeEntry.date;
  
  // 1. Prüfen, ob ein Dienstplan-Eintrag für die Zeit vorliegt
  if (rosterData && rosterData.length > 0) {
    const rosterEntry = rosterData.find(entry => 
      entry.date === date || 
      (entry.date && entry.date.split('T')[0] === date)
    );
    
    if (rosterEntry) {
      return { 
        isConspicuous: false, 
        reason: 'Dienstplan-Eintrag vorhanden',
        referenceEntry: rosterEntry 
      };
    }
  }
  
  // 2. Prüfen, ob ein Jahresplaneintrag für den Tag vorliegt
  if (annualPlanData && annualPlanData.length > 0) {
    const annualEntry = annualPlanData.find(entry => 
      entry.date === date || 
      (entry.date && entry.date.split('T')[0] === date)
    );
    
    if (annualEntry) {
      return { 
        isConspicuous: false, 
        reason: 'Jahresplan-Eintrag vorhanden',
        referenceEntry: annualEntry 
      };
    }
  }
  
  // 3. Wenn beides nicht vorliegt, ist die Zeit auffällig
  return { 
    isConspicuous: true, 
    reason: 'Weder Dienstplan- noch Jahresplaneintrag vorhanden' 
  };
};

/**
 * Extrahiert und normalisiert die Datumskomponente aus einem Zeitstring in beliebigem Format
 * 
 * @param {string} timeString - Zeitstring in beliebigem Format
 * @returns {string} - Normalisierte Datumskomponente im Format "YYYY-MM-DD"
 */
export const extractNormalizedDate = (timeString) => {
  return normalizeTimeFormat(timeString).date;
};